import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { parsePhoneNumberFromString, CountryCode } from 'libphonenumber-js/mobile';
import {
  ContactError,
  ContactService,
  WHATSAPP_CONSENT_TEXT,
  WhatsAppConsentSource
} from '../../../../core/services/contact.service';
import {
  COUNTRY_DIAL_CODES,
  CountryDialCode,
  defaultCountryForLocale
} from '../../../onboarding/data/country-dial-codes';

export type PhoneSetupMode = 'setup' | 'change';
export type FlowStep = 'number' | 'code' | 'done';

export interface VerifiedEvent {
  masked: string;
  verifiedAt: string;
}

export interface FlowState {
  step: FlowStep;
  maskedNumber: string;
}

const CODE_LENGTH = 6;

@Component({
  selector: 'app-whatsapp-verify-flow',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './whatsapp-verify-flow.component.html',
  styleUrls: ['./whatsapp-verify-flow.component.scss']
})
export class WhatsAppVerifyFlowComponent implements OnInit, AfterViewInit, OnDestroy {
  // Assigned by DialogService from the open() payload.
  @Input() mode: PhoneSetupMode = 'setup';
  @Input() source: WhatsAppConsentSource = 'setup_prompt';
  @Input() currentMasked: string | null = null;
  @Input() legacyE164: string | null = null;
  @Input() countryIso2: string | null = null;
  @Input() allowSnooze = false;
  /** Inline use (no outer padding). */
  @Input() embedded = false;
  /** Render the code-step and current-number lead-in text (the dialog shows it in its header). */
  @Input() showHeading = false;
  /** Show a Done button on the success step (dialog); inline use auto-advances instead. */
  @Input() showDoneButton = false;
  /** Focus the number box on load. */
  @Input() autoFocus = true;
  /** Pending setup task ids ('country' | 'whatsapp') to highlight on step 1. */
  @Input() pending: string[] = [];

  @ViewChild('numberInput') numberInput?: ElementRef<HTMLInputElement>;
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChildren('codeBox') codeBoxes!: QueryList<ElementRef<HTMLInputElement>>;

  @Output() verified = new EventEmitter<VerifiedEvent>();
  @Output() snoozed = new EventEmitter<void>();
  @Output() dismissed = new EventEmitter<void>();
  @Output() stateChange = new EventEmitter<FlowState>();

  @HostBinding('class.is-embedded') get isEmbedded(): boolean {
    return this.embedded;
  }

  readonly consentText = WHATSAPP_CONSENT_TEXT;
  readonly countries = COUNTRY_DIAL_CODES;
  readonly codeSlots = Array.from({ length: CODE_LENGTH }, (_, i) => i);

  step: FlowStep = 'number';
  selectedCountry: CountryDialCode = defaultCountryForLocale();
  national = '';
  consent = false;
  touchedNumber = false;
  dropdownOpen = false;
  searchQuery = '';

  sending = false;
  confirming = false;
  resending = false;
  error: ContactError | null = null;

  verificationId = '';
  maskedNumber = '';
  digits: string[] = Array(CODE_LENGTH).fill('');
  attemptsLeft: number | null = null;
  expired = false;
  locked = false;
  resendSeconds = 0;
  /** Throttled announcement for screen readers (not every second). */
  countdownAnnouncement = '';
  doneMasked = '';

  private resendDeadline = 0;
  private timerId: ReturnType<typeof setInterval> | null = null;
  private destroyed = false;

  constructor(
    private contact: ContactService,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.prefill();
  }

  ngAfterViewInit(): void {
    if (this.autoFocus) setTimeout(() => this.focusFirst(), 0);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopTimer();
  }

  /** Closes the country list if open; true when it did (lets a host swallow Escape). */
  closeDropdown(): boolean {
    if (!this.dropdownOpen) return false;
    this.dropdownOpen = false;
    this.refresh();
    return true;
  }

  /** Whether a request is in flight (hosts disable their close button). */
  get requestInFlight(): boolean {
    return this.sending;
  }

  /** Back to step 1 (used by hosts for "Change number"). */
  restart(): void {
    this.step = 'number';
    this.error = null;
    this.emitState();
    this.refresh();
    setTimeout(() => this.numberInput?.nativeElement?.focus(), 0);
  }

  // ---------- derived ----------

  get busy(): boolean {
    return this.sending || this.confirming || this.resending;
  }

  get parsed() {
    const digits = this.national.replace(/[^\d]/g, '');
    if (!digits) return null;
    const p = parsePhoneNumberFromString(digits, this.selectedCountry.iso2 as CountryCode);
    return p ?? null;
  }

  get numberValid(): boolean {
    const p = this.parsed;
    if (!p || !p.isValid()) return false;
    const type = p.getType();
    return !type || type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE';
  }

  get numberError(): string {
    if (!this.touchedNumber) return '';
    if (!this.national.trim()) return 'Enter your mobile number.';
    return this.numberValid ? '' : "That doesn't look like a valid mobile number for this country.";
  }

  get canSend(): boolean {
    return this.numberValid && this.consent && !this.sending;
  }

  get code(): string {
    return this.digits.join('');
  }

  get filteredCountries(): CountryDialCode[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return this.countries;
    return this.countries.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dialCode.includes(q) || c.iso2.toLowerCase().includes(q)
    );
  }

  get needsCountry(): boolean {
    return this.pending.includes('country');
  }

  get needsNumber(): boolean {
    return this.pending.includes('whatsapp');
  }

  /** A number exists (pre-filled from legacy) but was never verified. */
  get unverifiedPrefill(): boolean {
    return !!this.legacyE164 && this.national.length > 0;
  }

  get codeDisabled(): boolean {
    return this.expired || this.locked || this.confirming;
  }

  // ---------- step 1 ----------

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
    if (this.dropdownOpen) {
      this.searchQuery = '';
      setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
    }
  }

  selectCountry(c: CountryDialCode): void {
    this.selectedCountry = c;
    this.dropdownOpen = false;
    this.error = null;
    setTimeout(() => this.numberInput?.nativeElement?.focus(), 0);
  }

  onNumberInput(): void {
    this.error = null;
  }

  sendCode(): void {
    this.touchedNumber = true;
    if (!this.canSend) return;
    const p = this.parsed!;
    this.sending = true;
    this.error = null;
    this.contact
      .startVerification({
        country_iso2: this.selectedCountry.iso2,
        phone: p.number, // E.164, normalised by libphonenumber-js
        consent: true,
        source: this.source
      })
      .subscribe({
        next: (res) => {
          this.sending = false;
          this.verificationId = res.verification_id;
          this.maskedNumber = res.e164_masked;
          this.beginCodeStep(res.resend_after);
          this.refresh();
        },
        error: (err) => {
          this.sending = false;
          this.error = this.contact.toContactError(err);
          this.refresh();
        }
      });
  }

  // ---------- step 2 ----------

  private beginCodeStep(resendAfter: string): void {
    this.step = 'code';
    this.emitState();
    this.digits = Array(CODE_LENGTH).fill('');
    this.attemptsLeft = null;
    this.expired = false;
    this.locked = false;
    this.error = null;
    this.startCountdown(resendAfter);
    setTimeout(() => this.focusBox(0), 0);
  }

  onDigitInput(index: number, event: Event): void {
    const el = event.target as HTMLInputElement;
    const clean = el.value.replace(/\D/g, '');
    if (!clean) {
      this.digits[index] = '';
      el.value = '';
      return;
    }
    if (clean.length > 1) {
      // Autofill / paste-by-IME delivered several digits at once.
      this.fillFrom(index, clean);
      return;
    }
    this.digits[index] = clean;
    el.value = clean;
    this.error = null;
    if (index < CODE_LENGTH - 1) this.focusBox(index + 1);
    this.maybeSubmit();
  }

  onDigitKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
      event.preventDefault();
      this.digits[index - 1] = '';
      this.focusBox(index - 1);
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusBox(index - 1);
    } else if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      event.preventDefault();
      this.focusBox(index + 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.confirm();
    }
  }

  onDigitPaste(index: number, event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text') ?? '';
    this.fillFrom(index, text.replace(/\D/g, ''));
  }

  private fillFrom(start: number, digits: string): void {
    if (!digits) return;
    // A full code pasted into any box fills from the first box.
    const from = digits.length >= CODE_LENGTH ? 0 : start;
    for (let i = 0; i < digits.length && from + i < CODE_LENGTH; i++) {
      this.digits[from + i] = digits[i];
    }
    this.error = null;
    this.syncBoxes();
    this.focusBox(Math.min(from + digits.length, CODE_LENGTH - 1));
    this.maybeSubmit();
  }

  private syncBoxes(): void {
    this.codeBoxes?.forEach((b, i) => (b.nativeElement.value = this.digits[i] || ''));
  }

  private maybeSubmit(): void {
    if (this.code.length === CODE_LENGTH && !this.codeDisabled) this.confirm();
  }

  confirm(): void {
    if (this.code.length !== CODE_LENGTH || this.codeDisabled) return;
    this.confirming = true;
    this.error = null;
    this.contact.confirm(this.verificationId, this.code).subscribe({
      next: (res) => {
        this.confirming = false;
        this.doneMasked = res.whatsapp_e164_masked;
        // Keep the account country in step with the number's country (best effort).
        if (!this.countryIso2) {
          this.contact.setCountry(this.selectedCountry.iso2).subscribe({ error: () => undefined });
        }
        this.step = 'done';
        this.stopTimer();
        this.emitState();
        this.refresh();
        setTimeout(() => this.focusFirst(), 0);
        this.verified.emit({ masked: this.doneMasked, verifiedAt: res.verified_at });
      },
      error: (err) => {
        this.confirming = false;
        const e = this.contact.toContactError(err);
        this.error = e;
        if (e.code === 'OTP_INVALID') {
          this.attemptsLeft = e.attemptsLeft ?? this.attemptsLeft;
          this.clearDigits();
        } else if (e.code === 'OTP_EXPIRED') {
          this.expired = true;
        } else if (e.code === 'OTP_LOCKED') {
          this.locked = true;
        } else if (e.code === 'WHATSAPP_NUMBER_IN_USE') {
          this.locked = true;
        }
        this.refresh();
        if (e.code === 'OTP_INVALID') setTimeout(() => this.focusBox(0), 0);
      }
    });
  }

  resend(): void {
    if (this.resendSeconds > 0 || this.resending || this.confirming) return;
    this.resending = true;
    this.error = null;
    this.contact.resend(this.verificationId).subscribe({
      next: (res) => {
        this.resending = false;
        this.expired = false;
        this.locked = false;
        this.attemptsLeft = null;
        this.clearDigits();
        this.startCountdown(res.resend_after);
        this.refresh();
        setTimeout(() => this.focusBox(0), 0);
      },
      error: (err) => {
        this.resending = false;
        const e = this.contact.toContactError(err);
        this.error = e;
        if (e.code === 'OTP_RATE_LIMITED' && e.retryAfterSeconds) {
          this.startCountdown(new Date(Date.now() + e.retryAfterSeconds * 1000).toISOString());
        }
        if (e.code === 'OTP_EXPIRED') this.expired = true;
        if (e.code === 'OTP_LOCKED') this.locked = true;
        this.refresh();
      }
    });
  }

  changeNumber(): void {
    if (this.busy) return;
    this.stopTimer();
    this.step = 'number';
    this.emitState();
    this.error = null;
    this.expired = false;
    this.locked = false;
    this.verificationId = '';
    setTimeout(() => this.numberInput?.nativeElement?.focus(), 0);
  }

  // ---------- countdown ----------

  private startCountdown(resendAfterIso: string): void {
    const t = Date.parse(resendAfterIso);
    this.resendDeadline = Number.isFinite(t) ? t : Date.now() + 60_000;
    this.stopTimer();
    this.tick();
    this.timerId = setInterval(() => this.tick(), 1000);
  }

  private tick(): void {
    const left = Math.max(0, Math.ceil((this.resendDeadline - Date.now()) / 1000));
    this.resendSeconds = left;
    // Announce only at coarse points so aria-live isn't noisy.
    if (left === 0) this.countdownAnnouncement = 'You can request a new code now.';
    else if (left % 15 === 0) this.countdownAnnouncement = `You can request a new code in ${left} seconds.`;
    if (left === 0) this.stopTimer();
    this.refresh();
  }

  private stopTimer(): void {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  // ---------- closing / a11y ----------

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.dropdownOpen && !(event.target as HTMLElement).closest?.('.country-picker')) {
      this.dropdownOpen = false;
    }
  }

  private focusFirst(): void {
    if (this.step === 'number') this.numberInput?.nativeElement?.focus();
    else if (this.step === 'code') this.focusBox(0);
    else this.host.nativeElement.querySelector<HTMLElement>('[data-autofocus]')?.focus();
  }

  private focusBox(i: number): void {
    const box = this.codeBoxes?.get(i)?.nativeElement;
    box?.focus();
    box?.select();
  }

  private clearDigits(): void {
    this.digits = Array(CODE_LENGTH).fill('');
    this.syncBoxes();
  }

  private prefill(): void {
    const iso = (this.countryIso2 || '').toUpperCase();
    const legacy = (this.legacyE164 || '').trim();
    const fromIso = iso ? this.countries.find((c) => c.iso2 === iso) : undefined;
    if (fromIso) this.selectedCountry = fromIso;
    if (legacy) {
      const p = parsePhoneNumberFromString(legacy.startsWith('+') ? legacy : `+${legacy}`);
      if (p?.country) {
        // Prefer the country of the legacy number when we list it.
        const match = this.countries.find((c) => c.iso2 === p.country);
        if (match && (!fromIso || fromIso.dialCode === match.dialCode)) this.selectedCountry = match;
      }
      if (p && this.selectedCountry.dialCode === `+${p.countryCallingCode}`) {
        this.national = p.nationalNumber;
      }
    }
  }

  private refresh(): void {
    if (this.destroyed) return;
    this.cdr.detectChanges();
  }

  private emitState(): void {
    this.stateChange.emit({ step: this.step, maskedNumber: this.maskedNumber });
  }
}
